import * as React from 'react';

import type { W_Material_FindForJobImport_VM_Single } from 'ab-api/web/material/findForJobImport';

import { Button } from '@acceligentllc/storybook';

import SearchInput from 'af-components/Controls/SearchInput';

import MaterialEntriesLoading from './MaterialEntriesLoading';
import MaterialEntryCheckable from './MaterialEntryCheckable';
import styles from './styles.module.scss';

interface OwnProps {
	className?: string;
	isLoading: boolean;
	materials: Nullable<W_Material_FindForJobImport_VM_Single[]>;
	materialSearch: string;
	setImportForMultiple: (materials: W_Material_FindForJobImport_VM_Single[], value: boolean) => void;
	setMaterialSearch: (search: string) => void;
	toggleImport: (material: W_Material_FindForJobImport_VM_Single) => void;
}

type Props = OwnProps;

const MaterialsList = (props: Props) => {
	const {
		className,
		materials,
		toggleImport,
		setImportForMultiple,
		materialSearch,
		setMaterialSearch,
	} = props;

	const filteredMaterials = React.useMemo(() => {
		if (!materials) {
			return [];
		}
		if (!materialSearch) {
			return materials;
		}
		return materials.filter((_material) => (
			_material.displayName.toLowerCase().includes(materialSearch.toLowerCase())
			|| _material.description.toLowerCase().includes(materialSearch.toLowerCase())
			|| _material.vendor?.toLowerCase().includes(materialSearch.toLowerCase())
		));
	}, [materials, materialSearch]);

	const handleSelectAll = React.useCallback(() => {
		setImportForMultiple(filteredMaterials, true);
	}, [filteredMaterials, setImportForMultiple]);

	const handleDeselectAll = React.useCallback(() => {
		setImportForMultiple(filteredMaterials, false);
	}, [filteredMaterials, setImportForMultiple]);

	const isSelectAllDisabled = React.useMemo(() => filteredMaterials.every((_material) => _material.imported), [filteredMaterials]);

	const isDeselectAllDisabled = React.useMemo(() => filteredMaterials.every((_material) => !_material.imported), [filteredMaterials]);

	const resolvedClassName = React.useMemo(() => `${styles.MaterialsList} ${className ?? ''}`, [className]);

	const renderMaterial = React.useCallback((material: W_Material_FindForJobImport_VM_Single) => {
		return (
			<MaterialEntryCheckable key={material.id} material={material} toggleImport={toggleImport} />
		);
	}, [toggleImport]);

	const importedCount = React.useMemo(() => {
		if (!materials) {
			return 0;
		}
		return materials.reduce((_total, _mat) => {
			return _total + (_mat.imported ? 1 : 0);
		}, 0);
	}, [materials]);

	const totalCount = materials?.length ?? 0;
	const totalFilteredCount = filteredMaterials.length;
	const isFiltering = materialSearch.length > 0;

	return (
		<div className={resolvedClassName}>
			<div className={styles.controlsBar}>
				<SearchInput
					onChange={setMaterialSearch}
					placeholder="Search"
					value={materialSearch}
				/>
				<Button
					disabled={isSelectAllDisabled}
					label="Select All"
					onClick={handleSelectAll}
					style="link"
					type="button"
				/>
				<Button
					disabled={isDeselectAllDisabled}
					label="Deselect All"
					onClick={handleDeselectAll}
					style="link"
					type="button"
				/>
			</div>
			<div className={styles.list}>
				{!!materials ? filteredMaterials.map(renderMaterial) : <MaterialEntriesLoading />}
			</div>
			{totalCount > 0 && (
				<div className={styles.countersBar}>
					{
						(isFiltering && totalFilteredCount === 0)
							? <div className={styles.noSearchMatchWarning}>No materials matching "{materialSearch}".</div>
							: <div />
					}
					<div className={styles.counters}>Importing {importedCount} / {totalCount}</div>
				</div>
			)}
		</div>
	);
};

export default React.memo(MaterialsList);
